/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {ConnectionIssuesLinks} from '@app/components/ConnectionIssuesLinks';
import {FluxerIcon} from '@app/components/icons/FluxerIcon';
import {NativeDragRegion} from '@app/components/layout/NativeDragRegion';
import styles from '@app/components/layout/SplashScreen.module.css';
import {type SplashQuote, useSplashQuotes} from '@app/data/SplashQuotes';
import AccessibilityStore from '@app/stores/AccessibilityStore';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import GatewayConnectionStore from '@app/stores/gateway/GatewayConnectionStore';
import InitializationStore from '@app/stores/InitializationStore';
import StatusPageStore from '@app/stores/StatusPageStore';
import {getReducedMotionProps} from '@app/utils/ReducedMotionAnimation';
import {ExternalUrls} from '@fluxer/constants/src/ExternalUrls';
import {Trans} from '@lingui/react/macro';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

const PROBLEMS_DELAY = 10000;
const STATUS_PAGE_DISPLAY_DELAY = 5000;
type SplashScreenMode = 'live' | 'outage';

export const SplashScreen = observer(() => {
	const shouldBypass = DeveloperOptionsStore.bypassSplashScreen;
	const connected = GatewayConnectionStore.isConnected;
	const isInitialized = InitializationStore.canNavigateToProtectedRoutes;
	const isReady = connected && isInitialized;

	if (shouldBypass) return null;
	return <AnimatePresence initial={false}>{!isReady && <SplashScreenContent mode="live" />}</AnimatePresence>;
});

export const OutageSplashScreen = observer(() => <SplashScreenContent mode="outage" />);

const SPLASH_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.35},
};

const CONNECTION_ISSUES_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.4},
};

const INCIDENT_CTA_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.2},
};

interface SplashScreenContentProps {
	mode: SplashScreenMode;
}

function pickRandomQuote(quotes: ReadonlyArray<SplashQuote>): SplashQuote {
	return quotes[Math.floor(Math.random() * quotes.length)];
}

const SplashScreenContent = observer(({mode}: SplashScreenContentProps) => {
	const isOutageMode = mode === 'outage';
	const splashMotion = getReducedMotionProps(SPLASH_MOTION, AccessibilityStore.useReducedMotion);
	const connectionIssuesMotion = getReducedMotionProps(CONNECTION_ISSUES_MOTION, AccessibilityStore.useReducedMotion);
	const incidentCtaMotion = getReducedMotionProps(INCIDENT_CTA_MOTION, AccessibilityStore.useReducedMotion);
	const connected = isOutageMode ? false : GatewayConnectionStore.isConnected;
	const isInitialized = isOutageMode ? false : InitializationStore.canNavigateToProtectedRoutes;
	const isReady = connected && isInitialized;
	const splashQuotes = useSplashQuotes();
	const incident = StatusPageStore.incident;

	const quoteRef = useRef<SplashQuote | null>(null);
	if (quoteRef.current == null) {
		quoteRef.current = pickRandomQuote(splashQuotes);
	}
	const quote = quoteRef.current;
	const [showStatusData, setShowStatusData] = useState(isOutageMode);
	const [showProblems, setShowProblems] = useState(isOutageMode);
	const problemsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearProblemsTimer = useCallback(() => {
		if (problemsTimerRef.current != null) {
			clearTimeout(problemsTimerRef.current);
			problemsTimerRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (isOutageMode) {
			setShowStatusData(true);
			return;
		}
		const timer = setTimeout(() => setShowStatusData(true), STATUS_PAGE_DISPLAY_DELAY);
		return () => clearTimeout(timer);
	}, [isOutageMode]);

	useEffect(() => {
		if (isOutageMode) {
			clearProblemsTimer();
			setShowProblems(true);
			void StatusPageStore.checkIncidents();
			return;
		}

		if (isReady) {
			clearProblemsTimer();
			setShowProblems(false);
			return;
		}

		problemsTimerRef.current = setTimeout(() => {
			setShowProblems(true);
			void StatusPageStore.checkIncidents();
		}, PROBLEMS_DELAY);

		return clearProblemsTimer;
	}, [clearProblemsTimer, isOutageMode, isReady]);

	const liveIncident = showStatusData ? incident : null;

	const frozenDisplayRef = useRef<{incident: typeof liveIncident; text: string} | null>(null);
	if (!isReady) {
		frozenDisplayRef.current = {
			incident: liveIncident,
			text: liveIncident ? liveIncident.name : quote.text,
		};
	}
	const visibleIncident = frozenDisplayRef.current?.incident ?? liveIncident;
	const displayText = frozenDisplayRef.current?.text ?? quote.text;
	const incidentUrl = visibleIncident?.url ?? ExternalUrls.SERVICE_STATUS;

	if (isOutageMode) {
		return (
			<div className={styles.splashOverlay}>
				<NativeDragRegion className={styles.topDragRegion} />
				<div className={styles.splashContent}>
					<div className={styles.iconWrapper}>
						<div className={`${styles.iconPulse} ${styles.iconPulseStatic}`} />
						<FluxerIcon className={styles.icon} />
					</div>

					<div className={styles.quoteContainer}>
						{visibleIncident != null ? (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
								{displayText}
							</a>
						) : (
							<p className={styles.quote}>
								<Trans>Connection lost</Trans>
							</p>
						)}

						{visibleIncident != null && (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.incidentCta}>
								<Trans>View on status page</Trans>
							</a>
						)}
					</div>
				</div>

				{showProblems && !isReady && visibleIncident == null && (
					<div className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</div>
				)}
			</div>
		);
	}

	return (
		<motion.div {...splashMotion} className={styles.splashOverlay}>
			<NativeDragRegion className={styles.topDragRegion} />
			<div className={styles.splashContent}>
				<div className={styles.iconWrapper}>
					<div className={styles.iconPulse} />
					<FluxerIcon className={styles.icon} />
				</div>

				<div className={styles.quoteContainer}>
					{visibleIncident != null ? (
						<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
							{displayText}
						</a>
					) : (
						<>
							<p className={styles.quote}>{displayText}</p>
							<p className={styles.quoteSource}>{quote.source}</p>
						</>
					)}

					<AnimatePresence>
						{visibleIncident != null && (
							<motion.a
								{...incidentCtaMotion}
								href={incidentUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.incidentCta}
							>
								<Trans>View on status page</Trans>
							</motion.a>
						)}
					</AnimatePresence>
				</div>
			</div>

			<AnimatePresence>
				{showProblems && !isReady && visibleIncident == null && (
					<motion.div {...connectionIssuesMotion} className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
});
